import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi, fileUrl, API } from "../../lib/api";
import { useDialog } from "../../lib/dialog";
import { useEvents } from "../../lib/events";
import AdminLayout from "../../components/app/AdminLayout";
import { Loader } from "./Dashboard";
import { ArrowLeft, Search, Check, Lock, User, LogIn, LayoutGrid, List, Download, Drama, ShieldCheck, ClipboardCheck, Undo2, Calendar, MapPin } from "lucide-react";

const TYPE_LABEL = { dress_rehearsal: "Dress Rehearsal", safety_briefing: "Safety Briefing", other: "Session" };
const TYPE_ICON = { dress_rehearsal: Drama, safety_briefing: ShieldCheck, other: ClipboardCheck };

const fmtDate = (d) => {
  if (!d) return "No date set";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
};

export default function CastSessionDay() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const { isArchived } = useEvents();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState({});
  const [view, setView] = useState(() => localStorage.getItem("ssm_session_view") || "grid");
  const timer = useRef(null);

  const setViewMode = (v) => { setView(v); localStorage.setItem("ssm_session_view", v); };
  const load = () => adminApi.get(`/admin/cast-sessions/${sid}/day`).then(({ data }) => setData(data));
  useEffect(() => {
    load();
    timer.current = setInterval(load, 12000);
    return () => clearInterval(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  const s = data?.session;
  const closed = s?.closed;
  const locked = closed || isArchived;

  const signIn = async (id) => {
    setPending((p) => ({ ...p, [id]: true }));
    try { await adminApi.post(`/admin/cast-sessions/${sid}/signin/${id}`); await load(); }
    finally { setPending((p) => ({ ...p, [id]: false })); }
  };
  const undo = async (id) => {
    setPending((p) => ({ ...p, [id]: true }));
    try { await adminApi.post(`/admin/cast-sessions/${sid}/undo-signin/${id}`); await load(); }
    finally { setPending((p) => ({ ...p, [id]: false })); }
  };

  const closeSession = async () => {
    if (await dialog.confirm({ title: "Close this session?", description: "Anyone not signed in will be marked Absent and this board becomes read-only. This cannot be undone.", confirmLabel: "Close Session", destructive: true })) {
      await adminApi.post(`/admin/cast-sessions/${sid}/close`); await load();
    }
  };

  const exportCsv = () => { window.open(`${API}/admin/cast-sessions/${sid}/export`, "_blank"); };

  const filtered = useMemo(() => {
    let list = data?.attendees || [];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((a) => (a.full_name || "").toLowerCase().includes(q));
    return [...list].sort((a, b) => {
      const ai = a.attendance === "attended" ? 1 : 0;
      const bi = b.attendance === "attended" ? 1 : 0;
      if (ai !== bi) return ai - bi;
      return (a.full_name || "").localeCompare(b.full_name || "");
    });
  }, [data, search]);

  if (!data) return <AdminLayout title="Session"><Loader /></AdminLayout>;
  const Icon = TYPE_ICON[s.type] || ClipboardCheck;

  return (
    <AdminLayout title={s.name} subtitle={TYPE_LABEL[s.type] || "Session"}
      actions={<div className="flex items-center gap-2">
        <button onClick={() => navigate("/admin/rehearsals")} data-testid="session-back-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#3a3835] hover:text-[#F2EBE1]"><ArrowLeft size={14} /> Back</button>
        <button onClick={exportCsv} data-testid="session-export-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-blood hover:text-blood"><Download size={14} /> Export</button>
        {!locked && <button onClick={closeSession} data-testid="session-close-btn" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]"><Lock size={14} /> Close Session</button>}
      </div>}>

      {closed && <div className="mb-4 px-4 py-2.5 text-sm border border-[#262626] bg-[#0A0A0A] text-[#A19D94] flex items-center gap-2" data-testid="session-closed-banner"><Lock size={14} /> This session is closed and read-only.</div>}

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Stat label="Cast Expected" value={data.expected} testid="session-expected" />
        <Stat label="Signed In" value={data.signed_in} accent testid="session-signed-in" />
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-4 text-sm text-[#A19D94] flex flex-col justify-center gap-1">
          <span className="flex items-center gap-1.5"><Icon size={13} className="text-blood" /> {TYPE_LABEL[s.type] || "Session"}</span>
          <span className="flex items-center gap-1.5"><Calendar size={13} className="text-blood" /> {fmtDate(s.date)}</span>
          {s.location && <span className="flex items-center gap-1.5"><MapPin size={13} className="text-blood" /> {s.location}</span>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6862]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name…" data-testid="session-search"
            className="w-full bg-[#050505] border border-[#262626] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-blood" />
        </div>
        <div className="flex border border-[#262626]" data-testid="session-view-toggle">
          <button onClick={() => setViewMode("grid")} data-testid="session-view-grid" title="Grid view" className={`px-3 py-2.5 ${view === "grid" ? "bg-blood text-white" : "text-[#6B6862] hover:text-[#F2EBE1]"}`}><LayoutGrid size={15} /></button>
          <button onClick={() => setViewMode("list")} data-testid="session-view-list" title="List view" className={`px-3 py-2.5 border-l border-[#262626] ${view === "list" ? "bg-blood text-white" : "text-[#6B6862] hover:text-[#F2EBE1]"}`}><List size={15} /></button>
        </div>
      </div>

      {view === "grid" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.length === 0 && <p className="col-span-full text-sm text-[#6B6862] py-8 text-center">No cast members match.</p>}
          {filtered.map((a) => {
            const signed = a.attendance === "attended";
            return (
              <div key={a.id} data-testid={`session-attendee-${a.id}`} className={`border p-4 transition-colors ${signed ? "border-[#10B981] bg-[#10B981]/5" : "border-[#1a1a1a] bg-[#0A0A0A]"}`}>
                <div className="flex gap-3">
                  <div className="w-16 h-16 shrink-0 border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center">
                    {a.headshot_file_id ? <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt={a.full_name} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <User size={24} className="text-[#3a3835]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base uppercase tracking-tight leading-tight truncate">{a.full_name}</div>
                    <div className="text-[11px] text-[#6B6862] mt-0.5 space-x-2">
                      {a.age && <span>{a.age} yrs</span>}
                      {a.gender && <span>· {a.gender}</span>}
                    </div>
                    {a.roles?.length > 0 && <div className="text-[11px] text-[#A19D94] mt-0.5 truncate">{a.roles.join(", ")}</div>}
                  </div>
                </div>
                <div className="mt-3">
                  {signed ? (
                    <div className="flex items-center justify-between gap-2 py-2 px-2 text-xs uppercase tracking-wider text-[#10B981] border border-[#10B981]/40" data-testid={`session-signed-${a.id}`}>
                      <span className="flex items-center gap-1.5"><Check size={14} /> Signed in {a.signed_in_at ? `· ${a.signed_in_at.slice(11, 16)}` : ""}</span>
                      {!locked && <button onClick={() => undo(a.id)} disabled={pending[a.id]} data-testid={`session-undo-${a.id}`} title="Undo sign-in" className="text-[#6B6862] hover:text-blood"><Undo2 size={14} /></button>}
                    </div>
                  ) : (
                    <button onClick={() => signIn(a.id)} disabled={locked || pending[a.id]} data-testid={`session-signin-${a.id}`}
                      className="w-full flex items-center justify-center gap-1.5 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40">
                      <LogIn size={14} /> Sign In
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "list" && (
        <div className="border border-[#1a1a1a] bg-[#0A0A0A] divide-y divide-[#141414]" data-testid="session-list">
          {filtered.length === 0 && <p className="text-sm text-[#6B6862] py-8 text-center">No cast members match.</p>}
          {filtered.map((a) => {
            const signed = a.attendance === "attended";
            return (
              <div key={a.id} data-testid={`session-attendee-row-${a.id}`} className={`flex items-center gap-3 px-3 py-2 ${signed ? "bg-[#10B981]/5" : ""}`}>
                <div className="w-10 h-10 shrink-0 rounded-full border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center">
                  {a.headshot_file_id ? <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt={a.full_name} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <User size={18} className="text-[#3a3835]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm uppercase tracking-tight leading-tight truncate">{a.full_name}</div>
                  {a.roles?.length > 0 && <div className="text-[10px] text-[#6B6862] truncate">{a.roles.join(", ")}</div>}
                </div>
                <div className="shrink-0">
                  {signed ? (
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#10B981]" data-testid={`session-row-signed-${a.id}`}>
                      <Check size={14} /> In{a.signed_in_at ? ` ${a.signed_in_at.slice(11, 16)}` : ""}
                      {!locked && <button onClick={() => undo(a.id)} disabled={pending[a.id]} data-testid={`session-row-undo-${a.id}`} title="Undo" className="text-[#6B6862] hover:text-blood"><Undo2 size={13} /></button>}
                    </span>
                  ) : (
                    <button onClick={() => signIn(a.id)} disabled={locked || pending[a.id]} data-testid={`session-row-signin-${a.id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333] disabled:opacity-40">
                      <LogIn size={13} /> Sign In
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

const Stat = ({ label, value, accent, testid }) => (
  <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-4" data-testid={testid}>
    <div className="label-mini">{label}</div>
    <div className={`font-display text-4xl mt-1 ${accent ? "text-[#10B981]" : "text-[#F2EBE1]"}`}>{value}</div>
  </div>
);
